import { useTranslation } from 'react-i18next';

import { midiNoteName } from '@muzly/voice-engine';

import { freshHead } from './traceGeometry';

import type { PitchGraphPoint } from '../../../components';
import type { RangeSegmentId } from '../capture';

/**
 * PitchGaugeTrack — **저음·고음 진단**(`RecordViz: 'gauge'`)의 시각화. 세로 캡슐 게이지에
 * 표식이 오르내리고, **가운데에서 출발해** 지금까지 가장 멀리 간 지점까지 그라디언트로
 * 채워진다 — 표식이 내려갈수록(저음)/올라갈수록(고음) 채움이 표식을 따라 자란다(시안).
 * 극단 쪽에서 채우면 아직 못 간 구간이 이미 찬 것으로 읽힌다.
 *
 * 왜 곡선이 아니라 게이지인가: 이 진단이 재는 값은 "어디까지 갔나" 하나다. 시간축 곡선은
 * 지나간 궤적을 보여주지만, 그 궤적에서 사용자가 읽어야 할 것은 결국 **양 끝점**뿐이다.
 * 세로 한 축으로 그리면 화면의 위치가 곧 측정값이 된다.
 *
 * 채움이 **되돌아가지 않는다**: 저음 진단은 가장 낮은 지점까지, 고음은 가장 높은 지점까지
 * 채운 뒤 그대로 둔다. 순간값만 그리면 목표 음을 한 번 찍고 돌아온 순간 화면이 비어서,
 * 방금 낸 기록이 사라진 것처럼 보인다.
 *
 * 눈금은 시안대로 오른쪽에 `고음 / 중간 / 저음`을 세우고, **지금 음이름은 왼쪽**에 표식과
 * 같은 높이로 붙는다(사양: "좌측에 음정표시").
 *
 * **프롭 구동** — 구독처는 `useVoiceCapture` 하나뿐이다.
 */

/**
 * 게이지가 덮는 음역(MIDI). C2(36) ~ C6(84) — 4옥타브.
 *
 * 사람이 낼 수 있는 범위를 넉넉히 덮는다. 좁히면 아주 낮거나 높은 목소리가 끝에 붙어
 * 더 갈 수 있는지를 못 읽고, 넓히면 보통 목소리가 가운데 좁은 구간에서만 움직인다.
 */
export const GAUGE_LOW_MIDI = 36;
export const GAUGE_HIGH_MIDI = 84;

/** MIDI → 게이지 위치(0 = 아래 끝 = 저음, 1 = 위 끝 = 고음). */
export const gaugeRatio = (midi: number): number => {
  const span = GAUGE_HIGH_MIDI - GAUGE_LOW_MIDI;
  return Math.max(0, Math.min(1, (midi - GAUGE_LOW_MIDI) / span));
};

/** 채움의 출발점 — 게이지 가운데(`중간` 눈금과 같은 자리). 여기서 도달점까지만 채운다. */
const FILL_FROM_RATIO = 0.5;

/** 게이지 가운데 음 — 아직 아무 소리도 없을 때 표식이 서는 자리. */
const GAUGE_MID_MIDI = (GAUGE_LOW_MIDI + GAUGE_HIGH_MIDI) / 2;

interface PitchGaugeTrackProps {
  points: PitchGraphPoint[];
  /** 어느 방향으로 재는가. 채움이 아래로 자랄지 위로 자랄지를 정한다. */
  segment: RangeSegmentId;
  /** 이번 테이크에서 부른 폭 — 채움의 끝점을 여기서 읽는다(롤링 창이 아니라 전체다). */
  sung: { lo: number; hi: number } | null;
}

/** MIDI(연속값) → 사람이 읽는 음이름. 표시 전용이라 반올림해서 가장 가까운 음으로 붙인다. */
const midiToNoteLabel = (midi: number): string =>
  midiNoteName(Math.round(midi));

export const PitchGaugeTrack = ({
  points,
  segment,
  sung,
}: PitchGaugeTrackProps) => {
  const { t } = useTranslation();
  const live = freshHead(points)?.midi ?? null;
  // 채움의 끝 = 이 진단이 재는 방향의 극값. 아직 없으면 가운데(= 아무것도 못 갔다).
  const reached = segment === 'low' ? sung?.lo : sung?.hi;
  const cursorRatio = gaugeRatio(live ?? reached ?? GAUGE_MID_MIDI);
  const reachedRatio = reached != null ? gaugeRatio(reached) : null;

  return (
    <div
      role="img"
      aria-label={t('web.diagnosis.track.gaugeLabel')}
      // 위아래 여백이 없으면 양 끝의 눈금 라벨(`고음`·`저음`)이 절반만 보이고, 위쪽은
      // 상태 문구와 겹친다 — 라벨이 트랙 끝에 **중심을 맞춰** 놓이기 때문이다.
      className="relative flex w-full items-stretch justify-center py-5"
    >
      {/* 왼쪽 — 지금 음이름. 표식과 **같은 높이**로 따라 움직인다. */}
      <div className="relative w-24 shrink-0">
        {live != null && (
          <span
            data-testid="gauge-note"
            className="absolute right-2 -translate-y-1/2 text-base font-semibold tabular-nums"
            style={{ bottom: `${cursorRatio * 100}%` }}
          >
            {midiToNoteLabel(live)}
          </span>
        )}
      </div>

      {/* 게이지 — 캡슐 트랙 + 극값까지의 채움 + 표식. */}
      {/* 트랙은 **흰색**이다(시안) — 회색으로 두면 아직 안 간 구간이 "막힌 곳"으로 읽힌다.
          형태를 세우는 건 배경색이 아니라 둘레의 레드 글로우다. */}
      {/* `overflow-hidden`을 두지 않는다 — 표식이 이 안에 있고, 트랙 끝(저음 0% /
          고음 100%)에서 잘려 나간다. 글로우도 같이 사라진다. 채움은 자기
          `rounded-full`로 이미 캡슐 모양이라 클리핑이 필요 없다. */}
      <div className="relative w-10 shrink-0 rounded-full bg-[hsl(var(--surface-lowest))] shadow-[0_0_16px_rgba(251,35,59,0.22)]">
        {/* 채움은 **가운데에서 도달점까지**다(시안: 표식을 따라 자란다). 그라디언트의
            진한 끝은 도달점 쪽 — 채움의 머리가 표식과 같은 자리여야 한 몸으로 읽힌다. */}
        {reachedRatio != null && (
          <div
            data-testid="gauge-fill"
            aria-hidden="true"
            className={`absolute inset-x-0 rounded-full from-primary to-primary/10 motion-safe:transition-[bottom,height] motion-safe:duration-100 ${
              segment === 'low' ? 'bg-gradient-to-t' : 'bg-gradient-to-b'
            }`}
            style={
              segment === 'low'
                ? {
                    bottom: `${reachedRatio * 100}%`,
                    height: `${Math.max(0, FILL_FROM_RATIO - reachedRatio) * 100}%`,
                  }
                : {
                    bottom: `${FILL_FROM_RATIO * 100}%`,
                    height: `${Math.max(0, reachedRatio - FILL_FROM_RATIO) * 100}%`,
                  }
            }
          />
        )}
        <span
          data-testid="gauge-marker"
          // 흰 링이 있어야 표식이 **채움 위에서** 읽힌다 — 같은 레드끼리 겹치면 어디가
          // 지금 위치인지 사라진다(가로 트랙의 손잡이와 같은 처리).
          className={
            live == null
              ? 'absolute left-1/2 h-6 w-6 -translate-x-1/2 translate-y-1/2 rounded-full bg-muted-foreground/50 ring-4 ring-white'
              : 'absolute left-1/2 h-6 w-6 -translate-x-1/2 translate-y-1/2 rounded-full bg-primary ring-4 ring-white shadow-[0_0_0_9px_hsl(var(--primary)/0.18)] motion-safe:transition-[bottom] motion-safe:duration-100'
          }
          style={{ bottom: `${cursorRatio * 100}%` }}
        />
      </div>

      {/* 오른쪽 — 눈금. 이 진단이 노리는 끝만 브랜드색으로 세운다. */}
      <div className="relative w-24 shrink-0">
        {(
          [
            { key: 'high', at: 1 },
            // `중간` 눈금 = 채움의 출발점 — 둘이 갈라지면 채움이 눈금이 아닌 데서 시작한다.
            { key: 'mid', at: FILL_FROM_RATIO },
            { key: 'low', at: 0 },
          ] as const
        ).map(({ key, at }) => {
          const target =
            (segment === 'low' && key === 'low') ||
            (segment === 'high' && key === 'high');
          return (
            <div
              key={key}
              className="absolute left-2 flex -translate-y-1/2 items-center gap-2"
              style={{ bottom: `${at * 100}%` }}
            >
              <span
                aria-hidden="true"
                className={
                  target ? 'h-0.5 w-3.5 bg-primary' : 'h-0.5 w-3.5 bg-border'
                }
              />
              <span
                className={
                  target
                    ? 'text-sm font-semibold text-primary'
                    : 'text-sm text-muted-foreground'
                }
              >
                {t(`web.diagnosis.track.tick.${key}`)}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};
